import {
  ClientTestSettings,
  ClientTestSettingsValue,
  ClientTestUser,
  ClientUserRow,
} from "@/data/models/ClientAdministrationRow";
import { IRES_ADMIN_ACCOUNT_ID } from "@/store/modules/UserModule";

/**
 * The Test card's dialog (#2610070): what it sends, when it has a change, and the test users it
 * offers. Pure, so the rules are pinned by tests.
 */

/** One test user the dialog offers. */
export interface TestUserChoice {
  value: string;
  text: string;
}

/** The stored settings as the callable's `expected` / `value` side. */
export function toTestSettingsValue(settings: ClientTestSettings): ClientTestSettingsValue {
  return {
    isTestSystem: settings.isTestSystem,
    appVersion: settings.appVersion,
    roomInfoConfigVersion: settings.roomInfoConfigVersion,
    userIds: settings.testUsers.map((user) => user.id),
  };
}

/** Whether two settings differ; the order of the test users means nothing, as on IRES Cloud. */
export function testSettingsDiffer(left: ClientTestSettingsValue, right: ClientTestSettingsValue): boolean {
  return (
    left.isTestSystem !== right.isTestSystem ||
    left.appVersion !== right.appVersion ||
    left.roomInfoConfigVersion !== right.roomInfoConfigVersion ||
    left.userIds.length !== right.userIds.length ||
    left.userIds.some((id) => !right.userIds.includes(id))
  );
}

/** A user by first and last name, else the nickname; the nickname beside a full name when it differs. */
export function testUserLabel(user: Pick<ClientUserRow, "firstName" | "lastName" | "nickname" | "id">): string {
  const fullName = [user.firstName, user.lastName]
    .filter((part): part is string => !!part && part.trim() !== "")
    .map((part) => part.trim())
    .join(" ");
  const nickname = user.nickname?.trim() || "";
  if (fullName && nickname && nickname !== fullName) {
    return `${fullName} (${nickname})`;
  }
  return fullName || nickname || user.id;
}

/**
 * The test users the dialog offers: the client's users who are not deleted, the IRES admin user first
 * and the rest by name, as Mandant Test-App in IRES Office offered them. A stored test user missing
 * from that list stays offered at the end, so the selection still shows them and can drop them: one
 * deleted since (IRES Cloud refuses adding such a user), or any while the users are still loading or
 * failed to load. Whether one is deleted comes from IRES Cloud's `active`, never from being missing.
 *
 * @param users - the client's users.
 * @param current - the stored test users.
 * @param inactiveSuffix - appended to a deleted or missing test user's name ("(gelöscht)").
 * @param unknownName - the name of a test user no longer found.
 * @param selectedFirst - the users to put first, each group keeping the order above: the selection as
 *   it was when the list opened, never the live one, so a tick never moves rows under the pointer.
 */
export function testUserChoices(
  users: ClientUserRow[],
  current: ClientTestUser[],
  inactiveSuffix: string,
  unknownName: string,
  selectedFirst: string[] = []
): TestUserChoice[] {
  const active = users
    .filter((user) => !user.deleted)
    .map((user) => ({ value: user.id, text: testUserLabel(user) }))
    .sort((left, right) => {
      if ((left.value === IRES_ADMIN_ACCOUNT_ID) !== (right.value === IRES_ADMIN_ACCOUNT_ID)) {
        return left.value === IRES_ADMIN_ACCOUNT_ID ? -1 : 1;
      }
      return left.text.localeCompare(right.text);
    });
  const offered = new Set(active.map((choice) => choice.value));
  const leftOver = current
    .filter((user) => !offered.has(user.id))
    .map((user) => {
      const name = user.name ?? unknownName;
      return { value: user.id, text: user.active ? name : `${name} ${inactiveSuffix}` };
    });
  const choices = [...active, ...leftOver];
  return [
    ...choices.filter((choice) => selectedFirst.includes(choice.value)),
    ...choices.filter((choice) => !selectedFirst.includes(choice.value)),
  ];
}

/** The message for a refused or failed save. */
export function testSettingsErrorKey(error: unknown): string {
  const reason = (error as { details?: { reason?: unknown } | null } | null)?.details?.reason;
  if (reason === "CHANGED_MEANWHILE" || reason === "NO_CLIENT") {
    return `admin.clients.masterData.errors.${reason}`;
  }
  return reason === "UNKNOWN_TEST_USER" ? "admin.clients.testSettings.errors.UNKNOWN_TEST_USER" : "admin.clients.testSettings.saveFailed";
}
